import React, { useState, useEffect } from 'react';
import DatePicker from '../../../components/DatePicker';
import { API_URL } from '../constants';
import { authHeaders } from '../helpers';
import {
  BG_CARD, BORDER, INK, TEXT_INK,
  WARN_BG, WARN_BORDER, WARN_TEXT,
  DANGER, DANGER_SOFT,
} from '../styles';
import type { Schedule } from '../types';

const DatePickerAny = DatePicker as any;

interface Props {
  schedule: Schedule;
  onClose: () => void;
  onSaved: () => void;
}

export default function OverrideDateModal({ schedule, onClose, onSaved }: Props) {
  const [newDate, setNewDate] = useState<string>(schedule.next_pay_date || '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [draftCount, setDraftCount] = useState<number | null>(null);

  useEffect(() => {
    const checkDrafts = async (): Promise<void> => {
      try {
        const r = await fetch(API_URL + '/api/v1/payroll/runs?status=draft', {
          headers: authHeaders(),
        });
        if (r.ok) {
          const data = await r.json();
          setDraftCount(Array.isArray(data) ? data.length : 0);
        }
      } catch {
        setDraftCount(0);
      }
    };
    checkDrafts();
  }, []);

  const handleSave = async (): Promise<void> => {
    if (!newDate) { setError('Please choose a date'); return; }
    setSaving(true);
    setError('');
    try {
      const r = await fetch(API_URL + '/api/v1/payroll/schedules/' + schedule.id, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ first_pay_date: newDate }),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error('Save failed: ' + txt);
      }
      onSaved();
    } catch (e: any) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)', zIndex: 100,
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: BG_CARD, borderRadius: 14, padding: 24,
          maxWidth: 460, width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: INK, marginBottom: 6 }}>
          Override next pay date
        </div>
        <div style={{ fontSize: 13, color: TEXT_INK, marginBottom: 18, lineHeight: 1.5 }}>
          Manually set the next pay date. Useful for corrections, off-cycle payroll, or testing specific periods.
        </div>

        <div style={{
          background: WARN_BG, border: '1px solid ' + WARN_BORDER,
          borderRadius: 8, padding: '12px 14px', marginBottom: 18,
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <div style={{ color: WARN_TEXT, fontSize: 16, fontWeight: 700 }}>&#9888;</div>
          <div style={{ fontSize: 12, color: WARN_TEXT, lineHeight: 1.5 }}>
            This shifts the schedule anchor. Following pay dates will follow from this new date using the same frequency. Finalized pay runs are not affected.
          </div>
        </div>

        {draftCount !== null && draftCount > 0 && (
          <div style={{
            background: DANGER_SOFT, border: '1px solid #F0C4C4',
            borderRadius: 8, padding: '12px 14px', marginBottom: 18,
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <div style={{ color: DANGER, fontSize: 16, fontWeight: 700 }}>&#9888;</div>
            <div style={{ fontSize: 12, color: DANGER, lineHeight: 1.5 }}>
              You have {draftCount} draft pay run{draftCount === 1 ? '' : 's'}. They may become inconsistent after this change. Review the drafts page before saving.
            </div>
          </div>
        )}

        <div style={{ marginBottom: 18 }}>
          <label style={{
            display: 'block', fontSize: 11, fontWeight: 600, color: TEXT_INK,
            letterSpacing: 0.3, textTransform: 'uppercase', marginBottom: 8,
          }}>
            NEW NEXT PAY DATE
          </label>
          <DatePickerAny value={newDate} onChange={setNewDate} />
        </div>

        {error && (
          <div style={{
            background: DANGER_SOFT, color: DANGER,
            padding: '10px 12px', borderRadius: 8,
            fontSize: 12, marginBottom: 14,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'transparent', border: '1px solid ' + BORDER,
              color: INK, padding: '10px 18px', borderRadius: 10,
              fontSize: 13, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: INK, color: 'white', border: 'none',
              padding: '10px 18px', borderRadius: 10,
              fontSize: 13, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >{saving ? 'Saving...' : 'Save override'}</button>
        </div>
      </div>
    </div>
  );
}